// [PURPOSE]: Sub-plan 1C item 4 — pure-rules helpers for the RAW DMG p.138
//            cursed-item attunement lock.
//
// [WHY]: BASE_ITEMS already declares `cursed: true` on 5 magical-item rows
//        (sword_of_vengeance, berserker_axe, shield_of_missile_attraction,
//        demon_armor, bag_of_devouring) and InventoryItem (App.jsx:408) copies
//        the flag onto every instance via `this.isCursed = customData.isCursed
//        || baseItem.cursed || false`. But until this commit NO consumer
//        enforced the RAW invariant: "Attunement to a cursed item can't be
//        ended voluntarily unless the curse is broken first, such as with
//        the *remove curse* spell" (DMG p.138, verified 2026-05-18 against
//        H:/Projects/Tabletop/Dnd/5e Resources/Dungeon Master's Guide
//        (2014).md L5466-5470).
//
//        Pre-Sub-plan-1C-item-4, `InventoryContext.unattuneItem` un-attuned
//        any item including cursed ones — a multi-month silent gap. This
//        helper is the gate; consumers (InventoryContext, MagicItemsTab,
//        ContextMenuComponent) wire it in the same commit per Rule 25.
//
// [SCOPE — v1 minimum]: Pure cursed-flag check. No reveal-state map, no
//        confirm modal, no Remove-Curse integration, no migration, no DM
//        toggle. Per Spec 5 OQ-2 recommendation: blocked regardless of
//        reveal — the curse is mechanically real even if the surprise UX
//        hasn't fired. Reveal/modal/banner/Remove-Curse all deferred to
//        v1.x in docs/plans/COMING_SOON_ROADMAP.md.
//
// [RULE 11]: Property-based detection. We read `item.isCursed`, never match
//        on item names.
//
// [SPEC]: docs/superpowers/specs/2026-05-18-cursed-item-ui-gate-design.md (D4).

/**
 * Returns true if the character may voluntarily end attunement on this item.
 * Returns false ONLY when the item carries a truthy `isCursed` flag —
 * matching RAW DMG p.138.
 *
 * Defensive: null / undefined / non-object items return true (nothing to
 * lock). Consumers are expected to gate on `item.attuned` upstream; this
 * helper is orthogonal to attuned-state so a future equip-flow consumer
 * can reuse it.
 *
 * @param {object|null|undefined} item - An InventoryItem-shaped object.
 * @returns {boolean} True if voluntary un-attune is allowed.
 */
export function canUnattuneItem(item) {
  if (!item || typeof item !== 'object') return true;
  return !item.isCursed;
}

/**
 * Returns the user-facing showStatus copy when an un-attune attempt is
 * blocked by a curse. Surfaces the item name + cites the Remove Curse
 * remedy (RAW DMG p.138).
 *
 * @param {object|null|undefined} item - The item being unattuned from.
 * @returns {string} A status-bar message ready for `showStatus(...)`.
 */
export function getCurseUnattuneBlockedMessage(item) {
  const name = (item && typeof item === 'object' && item.name) || 'this cursed item';
  return `🔒 Cannot un-attune from ${name} — the curse binds you. Cast Remove Curse (or similar magic) to break free. DMG p.138.`;
}
